import { ProfileQueryKeys } from '../../profile/types/query-keys'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AcceptDeclineConnectionDTO,
  BlockConnectionDTO,
  ConnectionRequestDTO,
  UnblockConnectionDTO,
} from '../../profile/types/connections'
import { ClientMethodsTypes, client } from '@/services/v2/client'
import { User, UserQueryKeys } from 'containers/user/types/user'
import { useUser } from 'containers/user/hooks/useUser'
import { useProfile } from 'containers/profile/hooks/useProfile'

const endpointUrl = '/api/v1/users/connections'

export const useCleanConnection = () => {
  const queryClient = useQueryClient()
  const { user } = useUser()
  const { profile } = useProfile()

  return async () => {
    await queryClient.invalidateQueries([UserQueryKeys.USER])
    Promise.all([
      queryClient.invalidateQueries([
        ProfileQueryKeys.PROFILE,
        user?.username?.toLocaleLowerCase(),
      ]),
      profile?.id !== user?.id &&
        queryClient.invalidateQueries([
          ProfileQueryKeys.PROFILE,
          profile?.username?.toLocaleLowerCase(),
        ]),
      profile?.id !== user?.id &&
        queryClient.invalidateQueries([ProfileQueryKeys.CONNECTIONS]),
      queryClient.invalidateQueries([UserQueryKeys.CONNECTIONS]),
    ])
  }
}

export const useRequestConnection = () => {
  const cleanUserConnection = useCleanConnection()

  return useMutation({
    mutationFn: (connectionRequestData: ConnectionRequestDTO) => {
      return client<User>(endpointUrl + '/request', {
        method: ClientMethodsTypes.PATCH,
        body: {
          userIdReceivedRequest: connectionRequestData.profile?.id,
          message: connectionRequestData?.message,
        },
      })
    },
    onSuccess: cleanUserConnection,
  })
}

export const useAcceptConnection = () => {
  const cleanUserConnection = useCleanConnection()

  return useMutation({
    mutationFn: (connectionRequestData: AcceptDeclineConnectionDTO) => {
      return client<User>(endpointUrl + '/complete', {
        method: ClientMethodsTypes.PATCH,
        body: connectionRequestData,
      })
    },
    onSuccess: cleanUserConnection,
  })
}

export const useDeclineConnection = () => {
  const cleanUserConnection = useCleanConnection()

  return useMutation({
    mutationFn: (connectionRequestData: AcceptDeclineConnectionDTO) => {
      return client<User>(endpointUrl + '/decline', {
        method: ClientMethodsTypes.PATCH,
        body: connectionRequestData,
      })
    },
    onSuccess: cleanUserConnection,
  })
}

export const useRemoveConnection = () => {
  const cleanUserConnection = useCleanConnection()

  return useMutation({
    mutationFn: (connectionRequestData: AcceptDeclineConnectionDTO) => {
      return client<User>(endpointUrl + '/delete', {
        method: ClientMethodsTypes.PATCH,
        body: connectionRequestData,
      })
    },
    onSuccess: cleanUserConnection,
  })
}

export const useBlockConnection = () => {
  const cleanUserConnection = useCleanConnection()

  return useMutation({
    mutationFn: (connectionRequestData: BlockConnectionDTO) => {
      return client<User>(endpointUrl + '/block', {
        method: ClientMethodsTypes.PATCH,
        body: connectionRequestData,
      })
    },
    onSuccess: cleanUserConnection,
  })
}

export const useUnblockConnection = () => {
  const cleanUserConnection = useCleanConnection()

  return useMutation({
    mutationFn: (connectionRequestData: UnblockConnectionDTO) => {
      return client<User>(endpointUrl + '/unblock', {
        method: ClientMethodsTypes.PATCH,
        body: connectionRequestData,
      })
    },
    onSuccess: cleanUserConnection,
  })
}
